import React from "react";
import MegaHeader from "../MegaHeader";
import { imageUrl } from "../../utils/imagePath";
import "./AI-Powered Medical Research Intelligence Platform.scss";

const IMG = (file) =>
  imageUrl("competitive-landscape-monitoring-platform", file);

const MRIP_IMG = (file) =>
  imageUrl("aI-powered-medical-research-intelligence-platform", file);

const HERO_IMG = imageUrl(
  "aI-powered-medical-research-intelligence-platform",
  "ai-hero.svg"
);
const CHECK_MARK_IMG = imageUrl(
  "aI-powered-medical-research-intelligence-platform",
  "check-mark.svg"
);
const FOOTER_IMG = imageUrl(
  "aI-powered-medical-research-intelligence-platform",
  "footer-image.svg"
);

const EMBED_URL = "https://med-nav-hub-25.lovable.app/workshops";

const AiPoweredMedicalResearchIntelligencePlatform = () => {
  return (
    <>
      <MegaHeader />

      <section
        className="mrip-hero"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
        }}
      >
        <div className="mrip-hero__backdrop" aria-hidden="true">
          <span className="mrip-hero__orb mrip-hero__orb--one" />
          <span className="mrip-hero__orb mrip-hero__orb--two" />
          <span className="mrip-hero__grid" />
        </div>
        <div className="mrip-hero__content">
          <h1 className="mrip-hero__title">
            AI-Powered Medical Research Intelligence Platform
          </h1>
          <p className="mrip-hero__subtitle">
            Transform expert workshop conversations into structured insights, enabling research teams to quickly identify patterns and make faster strategic decisions.
          </p>
        </div>
      </section>

      <section className="mrip-section mrip-challenge">
        <h2 className="mrip-section__title">The Challenge</h2>
        <p className="mrip-section__intro">
          Medical research workshops generate valuable insight from healthcare
          experts, but these insights are often stored in transcripts, notes,
          and recordings across different tools.
        </p>

        <div className="mrip-challenge-grid">
          <div className="mrip-challenge-card">
            <div className="mrip-challenge-card__icon-wrap">
              <img
                src={MRIP_IMG("clock.svg")}
                alt=""
                className="mrip-challenge-card__icon"
              />
            </div>
            <p className="mrip-challenge-card__text">
              Time-consuming manual review of workshop transcripts
            </p>
          </div>

          <div className="mrip-challenge-card">
            <div className="mrip-challenge-card__icon-wrap">
              <img
                src={MRIP_IMG("book.svg")}
                alt=""
                className="mrip-challenge-card__icon"
              />
            </div>
            <p className="mrip-challenge-card__text">
              Insights scattered across multiple files and platforms
            </p>
          </div>

          <div className="mrip-challenge-card">
            <div className="mrip-challenge-card__icon-wrap">
              <img
                src={MRIP_IMG("search.svg")}
                alt=""
                className="mrip-challenge-card__icon"
              />
            </div>
            <p className="mrip-challenge-card__text">
              Difficulty tracing summarised insights back to original discussions
            </p>
          </div>

          <div className="mrip-challenge-card">
            <div className="mrip-challenge-card__icon-wrap">
              <img
                src={MRIP_IMG("pc.svg")}
                alt=""
                className="mrip-challenge-card__icon"
              />
            </div>
            <p className="mrip-challenge-card__text">
              Slow consolidation of insights across multiple workshops
            </p>
          </div>

          <div className="mrip-challenge-card mrip-challenge-card--centered">
            <div className="mrip-challenge-card__icon-wrap">
              <img
                src={MRIP_IMG("user.svg")}
                alt=""
                className="mrip-challenge-card__icon"
              />
            </div>
            <p className="mrip-challenge-card__text">
              Limited collaboration and contextual feedback during analysis
            </p>
          </div>
        </div>
      </section>

      <section className="mrip-section mrip-solution">
        <h2 className="mrip-section__title">Solution</h2>
        <p className="mrip-section__intro">
          The AI-powered platform organizes workshop data and converts expert
          discussions into structured insights. By combining transcripts,
          survey responses, and workshop metadata, the solution enables
          researchers to uncover conversation patterns, review summaries, and
          trace insights back to source evidence through an embedded dashboard.
        </p>

        <div className="mrip-solution-link-row">
          <a
            href={EMBED_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mrip-demo-link"
            aria-label="View medical research intelligence demo in a new tab"
          >
            <span className="mrip-demo-link__label">View Demo</span>
            <img
              src={IMG("link.svg")}
              className="mrip-demo-link__icon"
              alt=""
              aria-hidden="true"
            />
          </a>
        </div>

        <div className="mrip-solution-embed-wrapper">
          <iframe
            src={EMBED_URL}
            title="Medical Research Intelligence Platform"
            className="mrip-iframe"
            allowFullScreen
            allow="fullscreen"
            scrolling="yes"
          />
        </div>
      </section>

      <section className="mrip-section mrip-features">
        <h2 className="mrip-section__title">Key Features</h2>
        <div className="mrip-features-grid">
          <div className="mrip-feature-card">
            <div className="mrip-feature-card__icon-wrap">
              <img
                src={MRIP_IMG("workshop.svg")}
                alt=""
                className="mrip-feature-card__icon"
              />
            </div>
            <h3 className="mrip-feature-card__title">
              Centralised Workshop Repository
            </h3>
            <p className="mrip-feature-card__text">
              Store and manage all workshop discussions, transcripts, and
              metadata in one structured platform.
            </p>
          </div>

          <div className="mrip-feature-card">
            <div className="mrip-feature-card__icon-wrap">
              <img
                src={MRIP_IMG("books.svg")}
                alt=""
                className="mrip-feature-card__icon"
              />
            </div>
            <h3 className="mrip-feature-card__title">
              AI-Generated Workshop Summaries
            </h3>
            <p className="mrip-feature-card__text">
              Automatically convert workshop transcripts into structured
              summaries highlighting key insights from expert discussions, using
              GPT based processing.
            </p>
          </div>

          <div className="mrip-feature-card">
            <div className="mrip-feature-card__icon-wrap">
              <img
                src={MRIP_IMG("computer.svg")}
                alt=""
                className="mrip-feature-card__icon"
              />
            </div>
            <h3 className="mrip-feature-card__title">Insight Traceability</h3>
            <p className="mrip-feature-card__text">
              Each summary links directly to the original transcript segment,
              ensuring transparency and validation of insights.
            </p>
          </div>

          <div className="mrip-feature-card">
            <div className="mrip-feature-card__icon-wrap">
              <img
                src={MRIP_IMG("book.svg")}
                alt=""
                className="mrip-feature-card__icon"
              />
            </div>
            <h3 className="mrip-feature-card__title">
              Series-Level Insight Analysis
            </h3>
            <p className="mrip-feature-card__text">
              Aggregate insights across multiple workshops to identify recurring
              themes and strategic implications.
            </p>
          </div>

          <div className="mrip-feature-card">
            <div className="mrip-feature-card__icon-wrap">
              <img
                src={MRIP_IMG("magnifier.svg")}
                alt=""
                className="mrip-feature-card__icon"
              />
            </div>
            <h3 className="mrip-feature-card__title">
              Advanced Search & Filtering
            </h3>
            <p className="mrip-feature-card__text">
              Quickly filter workshops by series, drug, or disease area to
              locate relevant insights.
            </p>
          </div>

          <div className="mrip-feature-card">
            <div className="mrip-feature-card__icon-wrap">
              <img
                src={MRIP_IMG("review.svg")}
                alt=""
                className="mrip-feature-card__icon"
              />
            </div>
            <h3 className="mrip-feature-card__title">
              Collaborative Insight Review
            </h3>
            <p className="mrip-feature-card__text">
              Highlight text, tag colleagues, and add contextual comments
              directly within summaries.
            </p>
          </div>

          <div className="mrip-feature-card mrip-feature-card--centered">
            <div className="mrip-feature-card__icon-wrap">
              <img
                src={MRIP_IMG("chat.svg")}
                alt=""
                className="mrip-feature-card__icon"
              />
            </div>
            <h3 className="mrip-feature-card__title">
              AI Knowledge Chat Interface
            </h3>
            <p className="mrip-feature-card__text">
              Ask questions and retrieve summarized insights from the entire
              workshop knowledge base using the AI chat assistant.
            </p>
          </div>
        </div>

        <div className="mrip-features-logos" aria-label="Platform technologies">
          <img src={MRIP_IMG("fastapi.svg")} alt="FastAPI" className="mrip-features-logos__item" />
          <img src={MRIP_IMG("sqla.svg")} alt="SQLAlchemy" className="mrip-features-logos__item" />
          <img src={MRIP_IMG("docker.svg")} alt="Docker" className="mrip-features-logos__item" />
          <img src={MRIP_IMG("postgre.svg")} alt="PostgreSQL" className="mrip-features-logos__item" />
          <img src={MRIP_IMG("azure.svg")} alt="Azure" className="mrip-features-logos__item" />
          <img src={MRIP_IMG("chatgpt.svg")} alt="OpenAI" className="mrip-features-logos__item" />
          <img src={MRIP_IMG("databricks.svg")} alt="Databricks" className="mrip-features-logos__item" />
        </div>
      </section>

      <section
        className="mrip-impact"
        style={{
          backgroundImage: `url(${FOOTER_IMG})`,
        }}
      >
        <div className="mrip-impact__overlay" aria-hidden="true" />
        <div className="mrip-impact__content">
          <h2 className="mrip-impact__title">Impact</h2>
          <ul className="mrip-impact__list">
            <li>
              <img src={CHECK_MARK_IMG} alt="" aria-hidden="true" className="mrip-impact__check" />
              <span>Faster generation of workshop insights through AI summaries</span>
            </li>
            <li>
              <img src={CHECK_MARK_IMG} alt="" aria-hidden="true" className="mrip-impact__check" />
              <span>Improved trust and transparency through source traceability</span>
            </li>
            <li>
              <img src={CHECK_MARK_IMG} alt="" aria-hidden="true" className="mrip-impact__check" />
              <span>Centralized access to medical research workshop knowledge</span>
            </li>
            <li>
              <img src={CHECK_MARK_IMG} alt="" aria-hidden="true" className="mrip-impact__check" />
              <span>Better collaboration through shared commenting and tagging</span>
            </li>
            <li>
              <img src={CHECK_MARK_IMG} alt="" aria-hidden="true" className="mrip-impact__check" />
              <span>Faster strategic decisions by identifying patterns across workshops</span>
            </li>
          </ul>
        </div>
      </section>
    </>
  );
};

export default AiPoweredMedicalResearchIntelligencePlatform;
